import clsx from 'clsx';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '../../../../shared/ui/icons';
import { IconButton } from '../../../../shared/ui/icon-button/IconButton';
import { useOverlayEffects } from '../../../../shared/hooks';
import scrollbarStyles from '../../../../shared/styles/scrollbarThin.module.css';
import { CHANNEL } from '../../constants/messageTemplateKeys';
import { buildPreviewDocument } from '../../lib/buildEmailShell';
import { handlePreviewLinkClick } from './lib/handlePreviewLinkClick';
import styles from './TemplatePreviewPanel.module.css';

// Renders the email body in an <iframe> so its @media rules are honoured and its selectors
// stay isolated from the app. Auto-sizes to content height (re-measured via ResizeObserver).
function EmailBodyFrame({ bodyHtml, customStyle }) {
  const frameRef = useRef(null);
  const observerRef = useRef(null);

  const srcDoc = useMemo(
    () => buildPreviewDocument(bodyHtml, customStyle),
    [bodyHtml, customStyle],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const handleLoad = useCallback(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc?.body) return;

    // scrollHeight is a whole number while the content height is fractional, so assigning it
    // straight leaves a sliver the frame scrolls on. No-op writes are skipped to spare the observer.
    let applied = 0;
    const resize = () => {
      const html = doc.documentElement;
      const height = Math.ceil(
        Math.max(
          html.scrollHeight,
          doc.body.scrollHeight,
          html.getBoundingClientRect().height,
          doc.body.getBoundingClientRect().height,
        ),
      );
      if (height === applied) return;
      applied = height;
      frame.style.height = `${height}px`;
    };
    resize();

    observerRef.current?.disconnect();
    // From the frame's own window: an observer constructed here never delivers notifications for
    // elements laid out inside the frame, so the height never followed the content.
    const FrameResizeObserver = frame.contentWindow?.ResizeObserver;
    if (typeof FrameResizeObserver === 'function') {
      observerRef.current = new FrameResizeObserver(resize);
      observerRef.current.observe(doc.body);
      observerRef.current.observe(doc.documentElement);
    }

    // The first measurement runs on fallback font metrics, and images have no height until loaded.
    doc.fonts?.ready?.then(resize).catch(() => {});
    doc.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', resize, { once: true });
    });

    // Links inside the frame would otherwise navigate the frame itself; open them in a new
    // tab instead, matching the in-app preview's handlePreviewLinkClick behaviour.
    doc.addEventListener('click', (event) => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor) return;
      event.preventDefault();
      window.open(anchor.href, '_blank', 'noreferrer');
    });
  }, []);

  return (
    <iframe
      ref={frameRef}
      className={clsx(styles.bodyFrame)}
      title="Email preview"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      sandbox="allow-same-origin"
    />
  );
}

export function TemplatePreviewPanel({ open, onClose, template }) {
  const titleId = useId();
  useOverlayEffects({ open, onClose });

  if (!open) return null;

  const {
    name = '',
    channel = CHANNEL.EMAIL,
    subjects = [],
    htmlContent = '',
    customStyle = '',
    disclosures = [],
    logoUrl,
    senderInfo,
  } = template ?? {};
  const isEmail = channel === CHANNEL.EMAIL;
  const isSms = channel === CHANNEL.SMS;

  return createPortal(
    <div className={clsx(styles.overlay)} role="presentation" onClick={onClose}>
      <div
        className={clsx(styles.panel)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={clsx(styles.header)}>
          <h2 id={titleId} className={clsx(styles.title)}>
            {name}
          </h2>
          <IconButton ariaLabel="Close" onClick={onClose} size="sm">
            <CloseIcon aria-hidden />
          </IconButton>
        </div>

        <div className={clsx(styles.templateScroll, scrollbarStyles.scrollbarThinY)}>
          <div className={clsx(styles.emailTemplate)}>
            {isEmail && subjects.length > 0 && (
              <div className={clsx(styles.emailSubjectsHeader)}>
                <div className={clsx(styles.subjectsLabel)}>Email Subject Lines</div>
                <ul className={clsx(styles.subjectsList)}>
                  {subjects.map((subject, i) => (
                    <li key={i}>{subject}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={clsx(styles.emailBody, !isEmail && styles.emailBodyLeft)}>
              {logoUrl && !isSms && (
                <img src={logoUrl} alt="Waythru" className={clsx(styles.logo)} />
              )}
              {htmlContent && isEmail ? (
                <EmailBodyFrame bodyHtml={htmlContent} customStyle={customStyle} />
              ) : null}
              {htmlContent && !isEmail ? (
                <div
                  className={clsx(styles.emailBodyContent)}
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                  onClick={handlePreviewLinkClick}
                />
              ) : null}
            </div>

            {isEmail && disclosures.length > 0 && (
              <div className={clsx(styles.emailFooter)}>
                {disclosures.map((disclosure, i) => (
                  <div key={i} className={clsx(styles.disclosureItem)}>
                    <p className={clsx(styles.disclosureText)}>{`{{${disclosure.name}}}`}</p>
                  </div>
                ))}
              </div>
            )}

            {isEmail && senderInfo && (
              <div className={clsx(styles.companyFooter)}>
                {senderInfo.name && <p className={clsx(styles.companyText)}>{senderInfo.name}</p>}
                {(senderInfo.address || senderInfo.city) && (
                  <p className={clsx(styles.companyText)}>
                    {[
                      senderInfo.address,
                      senderInfo.city,
                      [senderInfo.state, senderInfo.zip].filter(Boolean).join(' '),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
                {senderInfo.unsubscribePage ? (
                  <a
                    href={senderInfo.unsubscribePage}
                    className={clsx(styles.unsubscribeLink)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Unsubscribe
                  </a>
                ) : (
                  <span className={clsx(styles.unsubscribeLink)}>Unsubscribe</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
